import React from "react";
import {
  View,
  FlatList,
  Dimensions,
  ActivityIndicator,
  Text,
} from "react-native";
import tw from "twrnc";

import AnimatedCategoryCard from "../../components/AnimatedCategoryCard";
import { useAppTheme } from "../../design-system/ThemeProvider";

export type Category = {
  category_id: number;
  icon: string;
  name: string;
  description: string;
};

interface CategoryPickerProps {
  categories: Category[];
  loading?: boolean;
  onCategorySelect: (category: Category) => void;
  numColumns?: number;
  header?: React.ReactNode;
  emptyMessage?: string;
}

const CategoryPicker: React.FC<CategoryPickerProps> = ({
  categories,
  loading = false,
  onCategorySelect,
  numColumns, // Will be calculated dynamically
  header,
  emptyMessage = "No categories found.",
}) => {
  const { colors } = useAppTheme();
  console.log("CategoryPicker: Received categories:", categories);
  console.log("CategoryPicker: Loading state:", loading);
  
  const screenWidth = Dimensions.get("window").width;
  
  // Premium responsive column calculation
  const getOptimalLayout = () => {
    const horizontalPadding = 40; // Wider gutters on mobile for nicer edge padding
    const cardSpacing = 14; // Slightly increased spacing between cards
    const minCardWidth = 160; // Minimum card width for readability
    const maxCardWidth = 200; // Maximum card width for premium look
    
    // Calculate optimal number of columns based on screen width
    let optimalColumns;
    if (screenWidth < 600) {
      // Phones: always 2 columns for comfortable tap targets
      optimalColumns = 2;
    } else {
      // Tablets and larger: 3 columns
      optimalColumns = 3;
    }
    
    // Calculate card width ensuring it fits perfectly
    const totalSpacing = horizontalPadding + (cardSpacing * (optimalColumns - 1));
    const availableWidth = screenWidth - totalSpacing;
    const calculatedCardWidth = Math.floor(availableWidth / optimalColumns);
    
    // Ensure card width is within premium bounds
    const finalCardWidth = Math.min(Math.max(calculatedCardWidth, minCardWidth), maxCardWidth);
    
    return {
      columns: optimalColumns,
      cardWidth: finalCardWidth,
      spacing: cardSpacing
    };
  };
  
  const layout = getOptimalLayout();
  const dynamicColumns = numColumns || layout.columns;
  const cardWidth = layout.cardWidth;
  const listHeader =
    typeof header === "string" ? (
      <Text style={[tw`text-lg font-bold mb-2`, { color: colors.onSurface }]} data-oid="kfbexjd">
        {header}
      </Text>
    ) : (
      header
    );

  if (loading) {
    return (
      <View
        style={tw`flex-1 px-6 pt-12 justify-center`}
        data-oid="_y021xe"
      >
        <ActivityIndicator size="large" color={colors.primary} data-oid="jrv6754" />
      </View>
    );
  }
  if (!categories || categories.length === 0) {
    return (
      <View
        style={tw`flex-1 px-6 pt-12 justify-center items-center`}
        data-oid="a86lylz"
      >
        <Text style={[tw`text-xl mb-4 font-semibold`, { color: colors.onSurface }]} data-oid="cercwo9">
          {emptyMessage}
        </Text>
      </View>
    );
  }

  console.log("CategoryPicker: Screen width:", screenWidth);
  console.log("CategoryPicker: Card width:", cardWidth);
  console.log("CategoryPicker: Num columns:", numColumns);

  return (
    <View style={tw`flex-1`} data-oid="v-7:h8j">
      <FlatList
        data={categories}
        keyExtractor={(item) => item.category_id.toString()}
        numColumns={dynamicColumns}
        ListHeaderComponent={listHeader ? <View style={tw`pb-2`}>{listHeader}</View> : null}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingBottom: 32,
          paddingTop: 12,
        }}
        columnWrapperStyle={dynamicColumns > 1 ? {
          justifyContent: 'space-between',
          paddingHorizontal: 10,
          marginBottom: 12,
          alignItems: 'center',
        } : undefined}
        showsVerticalScrollIndicator={false}
        renderItem={({ item, index }) => {
          console.log("CategoryPicker: Rendering item:", item.name, "ID:", item.category_id, "at index:", index);
          return (
            <AnimatedCategoryCard
              name={item.name}
              icon={item.icon}
              description={item.description}
              categoryId={item.category_id}
              onPress={() => {
                onCategorySelect(item);
              }}
              index={index}
              style={{ 
                width: cardWidth, 
                aspectRatio: 0.85,
                marginBottom: 14,
              }}
              data-oid="j6nem.m"
            />
          );
        }}
        getItemLayout={(_, index) => ({
          length: cardWidth + 16,
          offset: (cardWidth + 16) * index,
          index,
        })}
        data-oid="hl_xr8s"
      />
    </View>
  );
};

export default CategoryPicker;
